import { StoreInterface, StateMutations, AppInfoInterface, AppPlatform } from '@/store';
import { UserStateActions, UserStateMutations } from '@/store/modules/user';
import { RegisterUserStateMutations } from '@/store/modules/registerUser';
import { AssetStateActions } from '@/types/v2/asset/enums';
import { MessagingStateActions } from '@/store/modules/messaging';

/**
 * The store commit/dispatch bindings used by App.vue, extracted from its `setup()`.
 *
 * This is pure boilerplate relocation: the returned object has exactly the same keys and
 * behavior as before, so App.vue can spread it into its setup return and continue to call
 * `this.setPushToken(...)` etc. unchanged. Keeping it here shrinks the App.vue god-component
 * and gives one place for the store wiring.
 */
export function useAppStoreActions(store: StoreInterface) {
  return {
    setPushToken: (pushToken: string) => store.commit(StateMutations.setPushToken, pushToken),
    setFirebaseId: (firebaseId: string) => store.commit(StateMutations.setFirebaseId, firebaseId),
    setAskedForBiometrics: (date: string) => store.commit(StateMutations.setAskedForBiometrics, date),
    setBiometricsEnabled: (bio: boolean) => store.commit(StateMutations.setBiometricsEnabled, bio),
    setBiometricsType: (type: string) => store.commit(StateMutations.setBiometricsType, type),
    setLoggedIn: (loggedIn: boolean) => store.commit(StateMutations.setLoggedIn, loggedIn),
    setTemporaryUrlSafe: (url: string) => store.commit(StateMutations.setTemporaryUrlSafe, url),
    setAppInfo: (appInfo: AppInfoInterface) => store.commit(StateMutations.setAppInfo, appInfo),
    setEmptyUser: () => store.commit(UserStateMutations.setEmptyUser),
    fetchAssets: () => store.dispatch(AssetStateActions.fetchAssets),
    loadNotifications: () => store.dispatch(MessagingStateActions.fetchInitialNotifications),
    loadUserData: (userData?: any) => store.dispatch(UserStateActions.loadUserData, userData),
    resetRegisteUserState: () => store.commit(RegisterUserStateMutations.resetState),
    setRegisterUserRegister: (user: any) => store.commit(RegisterUserStateMutations.setRegisterUserRegister, user),
    setFeatureFlags: (featureFlags: any) => store.commit(StateMutations.setFeatureFlags, featureFlags),
    setDefaultFee: (fee: any) => store.commit(StateMutations.setDefaultFee, fee),
    setKeyboardIsActive: (isActive: boolean) => store.commit(StateMutations.setKeyboardIsActive, isActive),
    setAppVersion: (appVersion: string) => store.commit(StateMutations.setAppVersion, appVersion),
    setIsAppActive: (active: boolean) => store.commit(StateMutations.setIsAppActive, active),
    setAppPlatform: (platform: AppPlatform) => store.commit(StateMutations.setAppPlatform, platform),
    setTemporaryTokenSafe: (token: string) => store.commit(StateMutations.setTemporaryTokenSafe, token),
    setDeviceInformation: (deviceInformation: { model: string; type: string; osVersion: string; manufacturer: string }) =>
      store.commit(StateMutations.setDeviceInformation, deviceInformation),
    setInstallParameter: (installParameters: object) => store.commit(StateMutations.setInstallParameter, installParameters),
    setLoadingSpinner: (isLoading: boolean) => store.commit(StateMutations.setLoadingSpinner, isLoading)
  };
}
